// React Dependencies
import React, { useState, useRef, useReducer } from 'react';

// External Dependencies
import moment from 'moment';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { toast } from 'react-toastify';

// Internal Dependencies
import { IsEmpty } from '../../common/checks';
import { Status } from '../../common/constants';
import MButton from '../common/MButton';
import Colours from '../../styles/colours';
import apiService from '../../services.js/apiService';
import DateRangePicker from '../global/form/DateRangePicker';
import MText from '../common/MText';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../common/util';

const initializeFocusValue = {
  nameFocus:    false,
  idFocus:      false,
  addressFocus: false,
};

const ContriPOITable = () => {
  const table = useRef(null);
  const columns = [
    { title: 'Id', field: 'id', filtering: false, width: '5%' },
    {
      title:     'Name',
      field:     'name',
      filtering: false,
      render:    (rowData) => (
        <MButton
          text={rowData.name}
          variant="text"
          textColour={Colours.Blue}
          onClick={() => window.open(`/admin/contriPoi/${rowData.id}`, '_blank')}
          style={{ justifyContent: 'flex-start' }}
        />
      ),
    },
    {
      title:     'Address',
      field:     'address',
      filtering: false,
      render:    (contriPoi) => {
        const maxLength = 50;
        if (!IsEmpty(contriPoi.address)) {
          return (contriPoi.address.length >= maxLength) ? `${contriPoi.address.substring(0, maxLength)}...` : contriPoi.address;
        }
      },
    },
    { title: 'Status', field: 'status', filtering: false },
    {
      title:  'Created At',
      field:  'createdAt',
      render: (rowData) => (rowData.createdAt
        ? formatDateToDayMonthNameYear(showDateInSGTime(rowData.createdAt), true)
        : '-'),
    },
  ];

  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { nameFocus, idFocus, addressFocus } = autoFocus;
  const [filters, setFilters] = useReducer((state, action) => ({ ...state, ...action }), {
    id:        '',
    name:      '',
    status:    '',
    address:   '',
    createdAt: [],
  });

  const fetchDataTable = (query) => new Promise((resolve) => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const params = {
          pageSize,
          page:           query.page,
          orderBy:        'id',
          orderDirection: 'DESC',
          ...filters,
        };

        const result = await apiService.getContriPOIByParams(params);

        const { rows, totalCount } = result;
        const { page } = query;

        resolve({
          data: rows,
          page,
          totalCount,
        });
      } catch (err) {
        toast.error(err.message);
        resolve({
          data:       [],
          page:       0,
          totalCount: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  });

  const handleSearch = (e) => {
    const { name: fieldName, value } = e.target;
    setFilters({ [fieldName]: value });
    setAutoFocus({ [`${fieldName}Focus`]: true });
  };

  const refreshFilterTable = () => {
    if (table.current) {
      table.current.onQueryChange({
        ...table.current.state.query,
        page: 0,
      });
    }
  };

  const setDatesFilter = (dates) => {
    if (!IsEmpty(dates) && dates.length > 1) {
      const dateFrom = moment(dates[0]).utcOffset(8, true);
      const dateTo = moment(dates[1]).utcOffset(8, true);

      if (dateFrom.isValid() && dateTo.isValid()) {
        setFilters({
          createdAt: [
            dateFrom.format('YYYY-MM-DD'),
            dateTo.format('YYYY-MM-DD'),
          ],
        });
        return;
      }
    }
    setFilters({ createdAt: [] });
  };

  const clearFilters = () => {
    setFilters({
      id:        '',
      name:      '',
      status:    '',
      address:   '',
      createdAt: [],
    });
    setAutoFocus(initializeFocusValue);
    refreshFilterTable();
  };

  return (
    <div>
      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        onChangeRowsPerPage={(size) => {
          setAutoFocus(initializeFocusValue);
          setPageSize(size);
        }}
        isLoading={loading}
        data={fetchDataTable}
        actions={[
          {
            icon:    'edit',
            tooltip: 'Edit Contribute Poi',
            onClick: (event, contriPoi) => {
              window.open(`/admin/contriPoi/${contriPoi.id}`, '_blank');
            },
          },
        ]}
        options={{
          pageSize,
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
        }}
        components={{
          Toolbar: () => (
            <div className="pt-3">
              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="17%">
                  <FormControl fullWidth>
                    <MButton
                      variant="outlined"
                      size="medium"
                      textColour={Colours.Primary}
                      onClick={clearFilters}
                      text="Clear Filters"
                    />
                  </FormControl>
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="8%">
                  <FormControl fullWidth>
                    <TextField
                      name="id"
                      label="Id"
                      type="number"
                      value={filters.id || ''}
                      onChange={(e) => handleSearch(e)}
                      autoFocus={idFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="20%">
                  <FormControl fullWidth>
                    <TextField
                      name="name"
                      label="Name"
                      value={filters.name || ''}
                      onChange={(e) => handleSearch(e)}
                      autoFocus={nameFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="20%">
                  <FormControl fullWidth>
                    <TextField
                      name="address"
                      label="Address"
                      value={filters.address || ''}
                      onChange={(e) => handleSearch(e)}
                      autoFocus={addressFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={filters.status || ''}
                      name="ContriPoiStatus"
                      onChange={(e) => { setFilters({ status: e.target.value }); }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value={Status.Pending}>{Status.Pending}</MenuItem>
                      <MenuItem value={Status.Approved}>{Status.Approved}</MenuItem>
                      <MenuItem value={Status.Rejected}>{Status.Rejected}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box p={1}>
                  <MText
                    size={10}
                    colour={Colours.Gray1}
                    text="Create At Date Range"
                  />
                  <DateRangePicker
                    callback={(d) => setDatesFilter(d)}
                    dateVal={
                      filters.createdAt && filters.createdAt.length > 1
                        ? [new Date(filters.createdAt[0]), new Date(filters.createdAt[1])]
                        : ['', '']
                    }
                  />
                </Box>

                <Box p={1} style={{ margin: 2 }} width="20%">
                  <FormControl fullWidth>
                    <MButton
                      variant="contained"
                      className="mt-3"
                      size="medium"
                      colour={Colours.Primary}
                      textColour={Colours.White}
                      onClick={() => refreshFilterTable()}
                      text="Search"
                    />
                  </FormControl>
                </Box>

              </Box>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default ContriPOITable;
